import {uuid} from "@/interfaces";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {emailPreflight, resendConfirmationEmail} from "@/app/auth/signup/signup.api.ts";
import type {Invite, InviteKind} from "@shyne/shared/api";

type WizardStep = "email" | "plain" | "orgInvite" | "individualInvite" | "teamMemberInvite";

const STEP_OF_INVITE_KIND: Record<InviteKind, WizardStep> = {
    team: "teamMemberInvite",
    worker: "individualInvite",
    subcontractor: "orgInvite",
    vendor: "orgInvite",
    customer: "orgInvite",
    vendorRelationship: "email",
};

interface WizardState {
    step: WizardStep;
    email: string | null;
    inviteToken: uuid | null;
    inviteKind: InviteKind | null;
    inviterName: string | null;
    inviteeName: string | null;
    loading: boolean;
    error: string | null;
    sendingConfirmationEmail: boolean;
}

const initialState: WizardState = {
    step: "email",
    email: null,
    inviteToken: null,
    inviteKind: null,
    inviterName: null,
    inviteeName: null,
    loading: false,
    error: null,
    sendingConfirmationEmail: false,
}

export const checkEmail = createAsyncThunk(
    "signupWizard/checkEmail",
    async (email: string) => await emailPreflight(email)
);

export const resendEmail = createAsyncThunk("signupWizard/resendEmail", async(email: string) => await resendConfirmationEmail(email))

const wizardSlice = createSlice({
    name: "signupWizard",
    initialState,
    reducers: {
        resetWizard: () => initialState,
        setStep(state, action: PayloadAction<WizardState["step"]>) {
            state.step = action.payload;
        },
        setOrgInvite(state, action: PayloadAction<{ token: uuid; invite: Invite }>) {
            const { token, invite } = action.payload;
            state.step = STEP_OF_INVITE_KIND[invite.kind];
            state.email = invite.email;
            state.inviteToken = token;
            state.inviteKind = invite.kind;
            state.inviterName = invite.inviterName || null;
            state.inviteeName = invite.name;
            state.error = null;
            state.loading = false;
        },
    },
    extraReducers: (b) =>
        b.addCase(checkEmail.pending, (s, a) => {
            s.loading = true;
            s.error = null;
            s.email = a.meta.arg;
        }).addCase(checkEmail.fulfilled, (s, a) => {
            s.loading = false;

            switch (a.payload.type) {
                case "org_invite":
                    // Preflight confirms an invite exists but never returns its
                    // token (a bearer capability). The invited user must continue
                    // via the link emailed to them, which carries the token in the
                    // URL (handled by useSignupInviteInit / setOrgInvite). Guide
                    // them there rather than dropping into plain signup, which
                    // would create a duplicate org.
                    s.step = "email";
                    s.error =
                        "You've been invited to Shyne. Please open the invite link we emailed you to finish signing up.";
                    break;

                default:
                    s.step = "plain";
            }
        }).addCase(checkEmail.rejected, (s, a) => {
            s.loading = false;
            s.error = a.error.message ?? "Failed";
        }).addCase(resendEmail.pending, (s) => {
            s.sendingConfirmationEmail = true;
        }).addCase(resendEmail.fulfilled, (s) => {
            s.sendingConfirmationEmail = false;
        })
});

export const {resetWizard, setStep, setOrgInvite} = wizardSlice.actions;
export default wizardSlice.reducer;